import React from 'react';
import { X, FileText, ImageIcon, Edit2, Loader2 } from 'lucide-react';
import { at } from '../../../i18nAdmin';
import { Language, NewsItem } from '../../../types';
import { FeedNewsCard } from '../../News/FeedNewsCard';

interface NewsModalsProps {
    isPreviewOpen: boolean;
    isPreviewAnimating: boolean;
    isEditOpen: boolean;
    isEditAnimating: boolean;
    isActionPending: boolean;
    selectedNews: NewsItem | null;
    editFormData: {
        title_ko: string;
        summary_ko: string;
        content_ko: string;
        type: string;
    };
    setEditFormData: (data: any) => void;
    onClosePreview: () => void;
    onCloseEdit: () => void;
    onUpdateNews: () => void;
    lang: Language;
}

export const AdminNewsModals: React.FC<NewsModalsProps> = ({
    isPreviewOpen, isPreviewAnimating, isEditOpen, isEditAnimating, isActionPending,
    selectedNews, editFormData, setEditFormData, onClosePreview, onCloseEdit, onUpdateNews, lang
}) => {
    if (!selectedNews) return null;

    return (
        <>
            {/* Preview Modal */}
            {isPreviewOpen && (
                <div className={`fixed inset-0 z-[400] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isPreviewAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClosePreview}></div>
                    <div className={`relative bg-white w-full max-w-lg rounded-t-[40px] md:rounded-[40px] shadow-2xl flex flex-col overflow-hidden h-[80vh] md:h-auto md:max-h-[85vh] transition-transform duration-700 ease-[0.32,0.72,0,1] ${isPreviewAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-6 border-b border-slate-100 flex items-center justify-between shrink-0">
                            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">{at(lang, 'preview')}</h3>
                            <button onClick={onClosePreview} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors shrink-0"><X size={24} /></button>
                        </header>
                        <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-50">
                            <div className="max-w-md mx-auto py-8 px-4 space-y-12">
                                <section>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center mb-4">{at(lang, 'appearanceInLiveFeed')}</p>
                                    <div className="bg-white shadow-xl rounded-3xl overflow-hidden border border-slate-100">
                                        <FeedNewsCard 
                                            item={selectedNews} 
                                            index={0} 
                                            lang={lang} 
                                            onClick={() => {}}
                                        />
                                    </div>
                                </section>

                                <section className="bg-white shadow-xl rounded-[40px] overflow-hidden border border-slate-100 flex flex-col">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center py-6 border-b border-slate-50 bg-slate-50/50">{at(lang, 'detailPagePreview')}</p>
                                    <div className="p-8 space-y-8">
                                        <div className="space-y-4">
                                            <h1 className="text-2xl font-black text-slate-900 leading-tight">{selectedNews.title}</h1>
                                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                <span>{selectedNews.source}</span>
                                                <span>·</span>
                                                <span>{selectedNews.time}</span>
                                            </div>
                                        </div>

                                        {selectedNews.image && (
                                            <div className="rounded-[32px] overflow-hidden aspect-[16/9] bg-slate-100 border border-slate-100">
                                                <img src={selectedNews.image} className="w-full h-full object-cover" alt="" />
                                            </div>
                                        )}

                                        <div className="bg-rose-50/50 p-6 rounded-3xl border-l-4 border-rose-500 space-y-2">
                                            <p className="text-[9px] font-black text-rose-500 uppercase tracking-widest">Key Briefing</p>
                                            <p className="text-sm font-bold text-slate-700 leading-relaxed">{selectedNews.summary}</p>
                                        </div>

                                        <div className="prose prose-slate max-w-none">
                                            <p className="text-slate-600 text-base leading-relaxed whitespace-pre-wrap font-medium">
                                                {selectedNews.content}
                                            </p>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {isEditOpen && (
                <div className={`fixed inset-0 z-[400] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isEditAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onCloseEdit}></div>
                    <div className={`relative bg-white w-full max-w-2xl rounded-t-[40px] md:rounded-[40px] shadow-2xl flex flex-col overflow-hidden h-[90vh] md:h-[85vh] transition-transform duration-700 ease-[0.32,0.72,0,1] ${isEditAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-8 border-b border-slate-100 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-500 flex items-center justify-center">
                                    <Edit2 size={24} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">{at(lang, 'editIntelligence')}</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">ID: {selectedNews.id}</p>
                                </div>
                            </div>
                            <button onClick={onCloseEdit} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors shrink-0"><X size={24} /></button>
                        </header>
                        
                        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar space-y-8">
                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">{at(lang, 'titleKo')}</label>
                                <input 
                                    type="text" 
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10"
                                    value={editFormData.title_ko}
                                    onChange={e => setEditFormData({...editFormData, title_ko: e.target.value})}
                                />
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">{at(lang, 'summaryKo')}</label>
                                <textarea 
                                    rows={4}
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10 resize-none"
                                    value={editFormData.summary_ko}
                                    onChange={e => setEditFormData({...editFormData, summary_ko: e.target.value})}
                                />
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">{at(lang, 'contentKo')}</label>
                                <textarea 
                                    rows={10}
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10 resize-none"
                                    value={editFormData.content_ko}
                                    onChange={e => setEditFormData({...editFormData, content_ko: e.target.value})}
                                />
                            </div>

                            <div className="space-y-4">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">{at(lang, 'sector')}</label>
                                <input 
                                    type="text" 
                                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 px-5 text-sm font-bold text-slate-900 focus:bg-white transition-all outline-none focus:ring-2 focus:ring-rose-500/10"
                                    value={editFormData.type}
                                    onChange={e => setEditFormData({...editFormData, type: e.target.value})}
                                />
                            </div>
                        </div>

                        <footer className="p-8 border-t border-slate-100 bg-slate-50/50 flex gap-3 shrink-0">
                            <button onClick={onCloseEdit} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest md:hover:text-slate-900 transition-colors">{at(lang, 'cancel')}</button>
                            <button 
                                onClick={onUpdateNews} 
                                disabled={isActionPending}
                                className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-slate-100 md:hover:bg-black transition-all active:scale-95 disabled:opacity-50"
                            >
                                {isActionPending ? <Loader2 className="animate-spin mx-auto" size={18} /> : (at(lang, 'saveChanges'))}
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </>
    );
};
